import { memo, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';
import type { CalendarAxis, CalendarMonthGroup } from '../model/calendarLayout';
import { CalendarRow } from './CalendarRow';

/** Výška sticky hlavičky PO–NE. Sticky popisky měsíců sedí přesně pod ní. */
export const WEEKDAY_HEADER_HEIGHT = 27;

interface CalendarSurfaceProps {
  groups: readonly CalendarMonthGroup[];
  axis: CalendarAxis;
  weekdayLabels: readonly string[];
  highlightedTermId: string | null;
  onOpenTerm: (termId: string) => void;
  renderDayAction?: (isoDay: string) => ReactNode;
  /** Scroll kontejner — workspace přes něj skáče na termín, zmenšená osa z něj čte posuv. */
  scrollRef: (element: HTMLDivElement | null) => void;
  /** Podkladová mřížka jednoho týdne — z ní se měří šířka dne. */
  onMeasure: (dayWidth: number) => void;
}

/**
 * Posuvná plocha kalendáře. V režimu „Týdny“ se posouvá svisle a nahoře drží sticky hlavička
 * PO–NE se sticky popisky měsíců pod ní; v režimu „Osa“ se posouvá vodorovně (kolečko myši se
 * překlápí na vodorovnou osu, Shift + kolečko zůstává svislé) a popisky měsíců nese pruh uvnitř řady.
 *
 * Je **memoizovaná**, a to ne pro parádu: workspace nad ní překresluje titulek podle měsíce, na
 * kterém výřez zrovna stojí, takže při rychlém posuvu mění stav několikrát za vteřinu. Bez memo by
 * s ním pokaždé šla dolů celá plocha — v plném okně skoro deset tisíc elementů. Všechny propy jsou
 * proto stabilní (memoizované modely a callbacky).
 */
function CalendarSurfaceImpl({
  groups,
  axis,
  weekdayLabels,
  highlightedTermId,
  onOpenTerm,
  renderDayAction,
  scrollRef,
  onMeasure,
}: CalendarSurfaceProps) {
  const isTimeline = axis === 'timeline';
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const measureRef = useRef(onMeasure);
  measureRef.current = onMeasure;

  const setScroller = useCallback(
    (element: HTMLDivElement | null) => {
      scrollerRef.current = element;
      scrollRef(element);
    },
    [scrollRef]
  );

  // Šířka dne se v režimu „Týdny“ neví dopředu (mřížka je 7 × 1fr), a přitom na ní stojí celý
  // layout boxů dopravy. Měříme ji tedy z první vykreslené řady a přeměřujeme při změně šířky okna.
  const setGrid = useCallback((element: HTMLDivElement | null) => {
    gridRef.current = element;
    if (element) measureRef.current(element.getBoundingClientRect().width / 7);
  }, []);

  useEffect(() => {
    if (isTimeline || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      const element = gridRef.current;
      if (element) measureRef.current(element.getBoundingClientRect().width / 7);
    });
    if (gridRef.current) observer.observe(gridRef.current);
    return () => observer.disconnect();
  }, [isTimeline, groups]);

  // Na ose točí kolečko vodorovně (Shift + kolečko = svisle) — jinak by se s myší nedalo posunout.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller || !isTimeline) return;
    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey) return;
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? scroller.clientWidth : 1;
      if (event.shiftKey) {
        const dy = (event.deltaY || event.deltaX) * (event.deltaMode === 2 ? scroller.clientHeight : unit); // prettier-ignore
        if (!dy) return;
        event.preventDefault();
        scroller.scrollTop += dy;
        return;
      }
      const dx =
        (Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY) * unit;
      if (!dx) return;
      event.preventDefault();
      scroller.scrollLeft += dx;
    };
    scroller.addEventListener('wheel', onWheel, { passive: false });
    return () => scroller.removeEventListener('wheel', onWheel);
  }, [isTimeline]);

  let gridAssigned = false;
  const takeGridRef = () => {
    // Na ose je šířka dne pevná (zoom), měřit není co.
    if (isTimeline || gridAssigned) return undefined;
    gridAssigned = true;
    return setGrid;
  };

  return (
    <div
      ref={setScroller}
      data-testid="calendar-month-grid"
      className={cn(
        'border-phx-border rounded-phx-md relative min-h-0 flex-1 border',
        isTimeline ? 'overflow-x-auto overflow-y-auto' : 'overflow-x-hidden overflow-y-scroll'
      )}
    >
      {!isTimeline && (
        <div
          className="bg-phx-surface border-phx-border sticky top-0 z-[6] grid grid-cols-7 border-b"
          style={{ height: WEEKDAY_HEADER_HEIGHT }}
        >
          {weekdayLabels.map((label) => (
            <div
              key={label}
              className="text-phx-text-2 text-phx-xs px-phx-4 tracking-phx-wide flex items-center font-semibold uppercase"
            >
              {label}
            </div>
          ))}
        </div>
      )}

      {groups.map((group) => (
        <div key={group.key} data-testid="calendar-month-block" data-month={group.key}>
          {group.showLabel && (
            <div
              className="bg-phx-surface-2 border-phx-border text-phx-text text-phx-md px-phx-4_5 sticky z-[5] border-b py-[5px] font-semibold"
              style={{ top: WEEKDAY_HEADER_HEIGHT }}
              data-testid="calendar-month-title"
            >
              {group.label}
            </div>
          )}
          {group.rows.map((row) => (
            <CalendarRow
              key={row.key}
              row={row}
              weekdayLabels={weekdayLabels}
              highlightedTermId={highlightedTermId}
              onOpenTerm={onOpenTerm}
              renderDayAction={renderDayAction}
              gridRef={takeGridRef()}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

export const CalendarSurface = memo(CalendarSurfaceImpl);
